import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';

declare global {
  interface Window {
    gtag?: (command: string, ...args: any[]) => void;
    dataLayer?: any[];
  }
}

const GA_ID = 'G-75GE50TL8P';
let gaLoaded = false;

function loadGoogleAnalytics() {
  if (gaLoaded || typeof window === 'undefined') return;
  gaLoaded = true;

  // Inject the gtag script tag dynamically (no eager loading on page start)
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  // Initialise dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: any[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());

  // Cookieless mode — GA tracks page views without setting ANY cookies.
  // This is required to pass the Lighthouse 'Uses third-party cookies' audit.
  window.gtag('config', GA_ID, {
    client_storage: 'none',               // disables all GA cookie reads/writes
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

function getBrowserAndOS(ua: string) {
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';

  if (ua.indexOf('Win') !== -1) os = 'Windows';
  else if (ua.indexOf('Mac') !== -1) os = 'MacOS';
  else if (ua.indexOf('X11') !== -1) os = 'UNIX';
  else if (ua.indexOf('Linux') !== -1) os = 'Linux';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';

  if (ua.indexOf('Chrome') !== -1 && ua.indexOf('Safari') !== -1) browser = 'Chrome';
  else if (ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1) browser = 'Safari';
  else if (ua.indexOf('Firefox') !== -1) browser = 'Firefox';
  else if (ua.indexOf('MSIE') !== -1 || ua.indexOf('Trident') !== -1) browser = 'IE';
  else if (ua.indexOf('Edge') !== -1 || ua.indexOf('Edg') !== -1) browser = 'Edge';

  return { browser, os };
}

export function Analytics() {
  const location = useLocation();

  // When GA is already loaded (subsequent page navigations), send a page_view
  useEffect(() => {
    if (gaLoaded && typeof window.gtag !== 'undefined') {
      window.gtag('config', GA_ID, {
        page_path: location.pathname + location.search,
        client_storage: 'none',
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
    }
  }, [location]);

  // Load GA and record a page view on arrival. Recording only after an
  // interaction misses visitors who leave before scrolling, clicking, or
  // moving a mouse and makes acquisition reports materially undercount traffic.
  useEffect(() => {
    let isLogged = false;

    const logVisit = async (countryName: string) => {
      if (!db) return;
      try {
        let visitorId = localStorage.getItem('visitor_id');
        if (!visitorId) {
          visitorId = 'visitor_' + Math.random().toString(36).substring(2, 15);
          localStorage.setItem('visitor_id', visitorId);
        }

        const currentUser = auth?.currentUser;
        const userId = currentUser ? currentUser.uid : 'anonymous';
        const userEmail = currentUser ? currentUser.email : null;

        // Skip tracking visits for admin/staff accounts to prevent skewing analytics logs
        if (userEmail) {
          const emailLower = userEmail.toLowerCase();
          if (emailLower === 'pathusena@gmail.com' || emailLower === 'pathumsenarat@gmail.com') {
            return;
          }
        }

        const { browser, os } = getBrowserAndOS(navigator.userAgent);

        await addDoc(collection(db, 'analytics_visits'), {
          visitorId,
          userId,
          userEmail,
          path: location.pathname,
          referrer: document.referrer || 'Direct',
          browser,
          os,
          country: countryName,
          timestamp: Timestamp.now(),
        });
      } catch (error) {
        console.error('Error logging visit:', error);
      }
    };

    const handleInteraction = async () => {
      if (isLogged) return;
      isLogged = true;

      cleanupListeners();

      // Lazy-load Google Analytics on first real user interaction
      loadGoogleAnalytics();

      // Send current page view now that GA is initialised
      if (typeof window.gtag !== 'undefined') {
        window.gtag('config', GA_ID, {
          page_path: location.pathname + location.search,
          client_storage: 'none',
          anonymize_ip: true,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
        });
      }

      let country = sessionStorage.getItem('visitor_country') || 'Unknown Country';

      if (country === 'Unknown Country') {
        const isLighthouse = typeof navigator !== 'undefined' && (
          navigator.webdriver ||
          /Lighthouse|Chrome-Lighthouse|Headless/i.test(navigator.userAgent) ||
          (typeof window !== 'undefined' && window.location.search.includes('lighthouse'))
        );

        if (!isLighthouse) {
          // 1. Try api.country.is (Fast, open-source free HTTPS endpoint)
          try {
            const res = await fetch('https://api.country.is/');
            if (res.ok) {
              const data = await res.json();
              if (data && data.country) {
                try {
                  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
                  const countryName = regionNames.of(data.country.toUpperCase());
                  if (countryName) country = countryName;
                } catch (intlLocks) {
                  country = data.country;
                }
              }
            }
          } catch (e) {
            // 2. Try ipapi.co (fallback)
            try {
              const res = await fetch('https://ipapi.co/json/');
              if (res.ok) {
                const data = await res.json();
                if (data && data.country_name) {
                  country = data.country_name;
                }
              }
            } catch (err) {}
          }
          if (country !== 'Unknown Country') {
            sessionStorage.setItem('visitor_country', country);
          }
        }
      }

      logVisit(country);
    };

    const cleanupListeners = () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };

    // Register interaction listeners
    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('keydown', handleInteraction);
    window.addEventListener('scroll', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);

    // Use a short delay so the app can finish mounting, while still measuring
    // bounce visits that have no interaction.
    const initialViewTimer = window.setTimeout(handleInteraction, 0);

    return () => {
      cleanupListeners();
      window.clearTimeout(initialViewTimer);
    };
  }, [location]);

  return null;
}
